import React, { useContext } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { Box, ButtonBase, Stack, SvgIconProps, Typography } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';

import { EventsContext } from 'context/events';
import { useAspenLogger } from 'hooks/useAspenLogger';

import { ActivityImage } from 'components/common/ActivityImage';
import { IconMotion, IconPackage, IconPerson, IconPet, IconSound, IconVehicle } from 'components/icons';
import { getEventTypes } from 'modules/events';
import { formatDuration } from 'modules/time';
import { ActivityState } from 'types/aspen/events';
import { AppDeviceLeaf } from 'types/common/leaf';

import styles from './eventsTile.module.scss';

dayjs.extend(duration);

const detectionTypeMap: Record<string, React.ComponentType<SvgIconProps>> = {
  person: IconPerson,
  package: IconPackage,
  vehicle: IconVehicle,
  pet: IconPet,
  sound: IconSound,
  motion: IconMotion,
};

interface EventsCardProps {
  event: ActivityState;
  leaf: AppDeviceLeaf | null;
}

export const EventsTile = ({ event, leaf }: EventsCardProps) => {
  const { setModalOpen } = useContext(EventsContext);
  const navigate = useNavigate();
  const { logAspenEvent } = useAspenLogger();

  const videoDuration = event.parameters?.videoMeta?.duration;

  const IconComponents = getEventTypes(event.parameters?.objectsDetected).map((type) => ({
    type,
    component: detectionTypeMap[type],
  }));

  return (
    <Box sx={{ position: 'relative' }}>
      <ButtonBase
        onClick={() => {
          logAspenEvent({
            log_id: 'iot.web.event.interaction',
            payload: {
              event_id: event.id,
              interaction: 'open',
            },
          });

          setModalOpen(true);

          navigate({
            to: '/events/',
            search: { eventId: event.id },
          });
        }}
        sx={{ textAlign: 'left' }}
        data-testid={`button-event-tile-${event.id}`}
      >
        <Stack spacing={1} className={styles.eventTile}>
          <ActivityImage activity={event.parameters} alt={`${event.id}-event`} />
          <Typography variant="h6">{leaf?.name || '(Unknown device)'}</Typography>
          <Stack className={styles.eventDescription} spacing={1} direction="row">
            <Typography variant="body2">
              {dayjs(event.timestamp).format('hh:mm A')}
              {videoDuration ? (
                <>
                  <Box component="span" className={styles.dotDivider} />
                  {formatDuration(videoDuration)}
                </>
              ) : null}
            </Typography>
            <Stack direction="row" spacing={1} className={styles.detectionIcons}>
              {IconComponents.map(({ type, component: IconComponent }) => (
                <IconComponent
                  key={`${event.id}-${type}`}
                  color="secondary"
                  sx={{ fontSize: 16 }}
                  data-testid={`icon-event-type-${type}`}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
      </ButtonBase>
    </Box>
  );
};
